import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Walkthrough, { WalkthroughRef } from './Walkthrough';
import { WalkthroughStep } from '../../interfaces/walkthrough';
import { WelcomeStep } from './steps/WelcomeStep';
import { SalesforceSetupStep } from './steps/SalesforceSetupStep';
import { McpSetupStep } from './steps/McpSetupStep';
import { CompletionStep } from './steps/CompletionStep';
import { RootState } from '../../app/store';

interface CustomerWalkthroughProps {
  visible?: boolean;
  onComplete?: (dontShowAgain: boolean) => void;
  onSkip?: () => void;
}

const CustomerWalkthrough: React.FC<CustomerWalkthroughProps> = ({
  visible = true,
  onComplete,
  onSkip,
}) => {
  const [orgConnected, setOrgConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedMcpClient, setSelectedMcpClient] = useState<
    string | undefined
  >();
  const walkthroughRef = useRef<WalkthroughRef>(null);

  const { credentials, org } = useSelector(
    (state: RootState) => state.organization
  );

  // Track initial credential count to detect new connections during walkthrough
  const [initialCredentialCount] = useState(credentials.length);

  // Update orgConnected state only when new credentials are added during this walkthrough
  useEffect(() => {
    if (credentials.length > initialCredentialCount) {
      setOrgConnected(true);
    }
  }, [credentials.length, initialCredentialCount]);

  const steps: WalkthroughStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Cirra AI',
      shortTitle: 'Welcome',
      content: <WelcomeStep />,
      canSkip: true,
      showProgress: true,
    },
    {
      id: 'salesforce-setup',
      title: 'Connect Your Salesforce Organization',
      shortTitle: 'Connect to Salesforce',
      content: (
        <SalesforceSetupStep
          onOrgConnected={() => setOrgConnected(true)}
          isConnected={orgConnected}
          existingOrgs={credentials}
          defaultOrgId={org?.id ?? null}
          onConnectingChange={setIsConnecting}
          onMoveToNext={() => walkthroughRef.current?.advanceStep()}
        />
      ),
      canSkip: false,
      showProgress: true,
    },
    {
      id: 'mcp-setup',
      title: 'Integrate with your MCP Client',
      shortTitle: 'Configure your MCP Client',
      content: <McpSetupStep onClientSelect={setSelectedMcpClient} />,
      canSkip: false,
      showProgress: true,
    },
    {
      id: 'completion',
      title: "You're All Set!",
      shortTitle: 'Complete',
      content: <CompletionStep />,
      canSkip: false,
      showProgress: true,
    },
  ];

  return (
    <Walkthrough
      ref={walkthroughRef}
      steps={steps}
      visible={visible}
      onComplete={onComplete}
      onSkip={onSkip}
      navigationDisabled={isConnecting}
      isStepComplete={(stepId: string) => {
        if (stepId === 'mcp-setup') {
          return !!selectedMcpClient;
        }
        return true;
      }}
    />
  );
};

export default CustomerWalkthrough;
